import { FC, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  CloseModalButton,
  BackModalButton,
  MainButton,
} from "components/Buttons";
import BasicModal from "./BasicModal";
import { ModalHeading, ModalContent, InfoDecorative } from "./styled";
import {
  CardImageContainer,
  CardBack,
  CardBackLine,
} from "pages/CryptoCardsPage/styled";
import {
  colors,
  virtualCardDesigns,
  extraVirtualCardDesigns,
} from "helpers/consts";
import { useMediaQuery } from "helpers/hooks";
import { format } from "helpers/funcs";
import { AppText } from "components";
import { setupMFAVerification } from "api/auth";
import { getCardSensitiveData } from "api/cardProduct";
import { GlobalStore, UserStore } from "stores";
import { authStepUp, waitForOneTimeAccessToken } from "api/auth";
import type {
  UsersCard,
  GetUsersCardSensitiveDataRes,
} from "api/types/cardProduct";

import { ReactComponent as LockIcon } from "assets/icons/lock-unlocked.svg";
interface CardDetailsModalProps {
  cardInfo?: UsersCard | null;
  isOpen: boolean;
  onClose: () => void;
}
const CardDetailsModal: FC<CardDetailsModalProps> = ({
  cardInfo,
  onClose,
  isOpen,
}) => {
  const laptop = useMediaQuery("(min-width: 30em)");
  const mobile = useMediaQuery("(min-width: 26em)");
  const { t } = useTranslation();

  const { isMFAEnabled, authOneTimeAccessToken } = UserStore;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fullCardInfo, setFullCardInfo] =
    useState<GetUsersCardSensitiveDataRes | null>(null);

  const change2Fa = useCallback(() => {
    setIsLoading(true);
    setupMFAVerification().finally(() => setIsLoading(false));
  }, [setIsLoading]);

  const getCardDetails = async () => {
    if (!cardInfo || fullCardInfo) {
      return;
    }
    try {
      await authStepUp("STEP_UP_CARD");

      await waitForOneTimeAccessToken();
      setIsLoading(true);

      const { data } = await getCardSensitiveData(
        cardInfo.card.id,
        authOneTimeAccessToken!
      );
      setFullCardInfo(data);
    } catch (err: any) {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCardDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BasicModal
      onExited={() => setFullCardInfo(null)}
      isOpen={isOpen}
      onClose={onClose}
      showLoader={isLoading}
    >
      <ModalContent>
        <BackModalButton onBack={onClose} label="back" />
        <CloseModalButton onClose={onClose} label="close" />
        <ModalHeading $centered>{t("CARD_DETAILS")}</ModalHeading>

        {!isMFAEnabled ? (
          <>
            <AppText fontSize={18} fontWeight={600}>
              {t("TWO_FA_FOR_DETAILS")}
            </AppText>

            <MainButton
              maxWidth="14rem"
              style={{ marginTop: "1.6rem" }}
              variant="contained"
              onClick={() => change2Fa()}
            >
              <LockIcon />
              {t("ENABLE")}
            </MainButton>
          </>
        ) : (
          <>
            {fullCardInfo && (
              <InfoDecorative $isAlternative>
                <CardImageContainer>
                  <CardBack
                    $bgColor={
                      cardInfo
                        ? cardInfo.card.designCode === "CA02"
                          ? extraVirtualCardDesigns[cardInfo.card.currencyName]
                              ?.bgColor || undefined
                          : virtualCardDesigns[cardInfo.card.designCode]
                              ?.bgColor || undefined
                        : undefined
                    }
                  >
                    <CardBackLine
                      $lineColor={
                        cardInfo
                          ? cardInfo.card.designCode === "CA02"
                            ? extraVirtualCardDesigns[
                                cardInfo.card.currencyName
                              ]?.lineColor || undefined
                            : virtualCardDesigns[cardInfo.card.designCode]
                                ?.lineColor || undefined
                          : undefined
                      }
                    />
                  </CardBack>

                  {mobile && cardInfo?.card.embossName && (
                    <>
                      <AppText
                        fontSize={laptop ? 14 : 12}
                        fontWeight={400}
                        color={colors.white}
                        style={{
                          bottom: laptop ? "17.2rem" : "13.6rem",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("CARD_HOLDER")}
                      </AppText>
                      <AppText
                        fontSize={laptop ? 24 : 16}
                        fontWeight={400}
                        color={colors.white}
                        style={{
                          bottom: laptop ? "14rem" : "11.6rem",
                        }}
                      >
                        {cardInfo.card.embossName}
                      </AppText>
                    </>
                  )}

                  <AppText
                    fontSize={laptop ? 14 : 12}
                    fontWeight={400}
                    color={colors.white}
                    style={{
                      bottom: laptop ? "11.5rem" : "8.7rem",
                    }}
                  >
                    {t("CARD_NUMBER")}
                  </AppText>
                  <AppText
                    fontSize={laptop ? 32 : 18}
                    fontWeight={400}
                    color={colors.white}
                    style={{
                      bottom: laptop ? "7.5rem" : "6.4rem",
                    }}
                  >
                    {format(
                      !fullCardInfo.cardNumber ||
                        fullCardInfo.cardNumber === "00000000000000000000"
                        ? cardInfo?.card.maskedCardNumber ||
                            "00000000000000000000"
                        : fullCardInfo.cardNumber,
                      "9999 9999 9999 9999"
                    )}
                  </AppText>

                  {fullCardInfo?.expiryDate && (
                    <>
                      <AppText
                        fontSize={laptop ? 14 : 12}
                        fontWeight={400}
                        color={colors.white}
                        style={{
                          bottom: laptop ? "5rem" : "3.8rem",
                        }}
                      >
                        {t("EXPIRY_DATE")}
                      </AppText>
                      <AppText
                        fontSize={laptop ? 34 : 18}
                        fontWeight={400}
                        color={colors.white}
                        style={{
                          bottom: laptop ? "1rem" : "1.6rem",
                        }}
                      >
                        {moment(fullCardInfo.expiryDate).format("M/YY")}
                      </AppText>
                    </>
                  )}

                  <AppText
                    fontSize={laptop ? 14 : 12}
                    fontWeight={400}
                    color={colors.white}
                    style={{
                      bottom: laptop ? "5rem" : "3.8rem",
                      left: "auto",
                      right: "1.6rem",
                    }}
                  >
                    {t("SECURITY_CODE")}
                  </AppText>
                  <AppText
                    fontSize={laptop ? 34 : 18}
                    fontWeight={400}
                    color={colors.white}
                    style={{
                      bottom: laptop ? "1rem" : "1.6rem",
                      left: "auto",
                      right: "1.6rem",
                    }}
                  >
                    {fullCardInfo.cvv || "***"}
                  </AppText>
                </CardImageContainer>
                {mobile === false && cardInfo?.card.embossName && (
                  <AppText
                    color={colors.gray_900}
                    style={{
                      margin: "1rem 0 0 0.4rem",
                      alignSelf: "flex-start",
                    }}
                  >
                    {t("CARD_HOLDER")}: <b>{cardInfo.card.embossName}</b>
                  </AppText>
                )}
              </InfoDecorative>
            )}
          </>
        )}
      </ModalContent>
    </BasicModal>
  );
};

export default observer(CardDetailsModal);
